// api/units.ts - UnitsController endpoints (owner/manager контекст)

import { AxiosResponse } from 'axios';
import $api from './instance';
import { GetVacantUnitsDashboardTableRow } from '../types/responses';
import {
  IUnitCharacteristics,
  UnitWithLeaseInfo,
  UnitLease,
  UnitTenant,
  DetailedUnitResponse,
  LeaseHistoryItem,
  VacantUnitFromApi,
} from '@/types/entities';
import {
  UnitsSearchParams,
  UnitsApiResponse,
  CreatePendingLeaseRequest,
  CreatePendingLeaseResponse,
  VacantUnitsApiResponse,
  VacantUnitsSearchParams,
  UnitOptionsResponse,
  SelectOption, // Import SelectOption from api-types
} from '@/types/api-types';

// === UNITS API ===

/**
 * Отримати опції для фільтрів юнітів
 * GET /units/options
 */
export async function getUnitOptionsRequest(): Promise<
  AxiosResponse<UnitOptionsResponse>
> {
  return $api.get<UnitOptionsResponse>('/units/options');
}

/**
 * Отримати список вільних юнітів
 * GET /units/vacant
 */
export async function getVacantUnitsRequest(
  params?: VacantUnitsSearchParams,
): Promise<AxiosResponse<VacantUnitsApiResponse>> {
  const searchParams = new URLSearchParams();

  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const url = queryString ? `/units/vacant?${queryString}` : '/units/vacant';

  return $api.get<VacantUnitsApiResponse>(url);
}

/**
 * Отримати список юнітів property з фільтрацією та пагінацією
 * GET /properties/:propertyId/units
 */
export async function getPropertyUnitsRequest(
  propertyId: number,
  params?: UnitsSearchParams,
): Promise<AxiosResponse<UnitsApiResponse>> {
  const searchParams = new URLSearchParams();

  if (params?.search) searchParams.set('search', params.search);
  if (params?.vacancy && params.vacancy !== 'all') {
    searchParams.set('vacancy', params.vacancy);
  }
  if (params?.paymentStatus && params.paymentStatus !== 'all') {
    searchParams.set('paymentStatus', params.paymentStatus);
  }
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params?.sortDirection) {
    searchParams.set('sortDirection', params.sortDirection);
  }

  const queryString = searchParams.toString();
  const url = queryString
    ? `/properties/${propertyId}/units?${queryString}`
    : `/properties/${propertyId}/units`;

  return $api.get<UnitsApiResponse>(url);
}

/**
 * Отримати юніт з повною інформацією
 * GET /properties/:propertyId/units/:unitId
 */
export async function getUnitWithDetailsRequest(
  propertyId: number,
  unitId: number,
): Promise<AxiosResponse<DetailedUnitResponse>> {
  return $api.get<DetailedUnitResponse>(
    `/properties/${propertyId}/units/${unitId}`,
  );
}

/**
 * Додати нові юніти до property
 * POST /properties/:propertyId/units
 */
export async function addUnitsToPropertyRequest(
  propertyId: number,
  units: { number: string; type: string }[],
): Promise<AxiosResponse<UnitWithLeaseInfo[]>> {
  return $api.post(`/properties/${propertyId}/units`, units);
}

// === LEASE API через UnitsController ===

/**
 * Отримати активний lease для юніта
 * GET /properties/:propertyId/units/:unitId/lease/active
 */
export async function getActiveLeaseForUnitRequest(
  propertyId: number,
  unitId: number,
): Promise<AxiosResponse<UnitLease | null>> {
  return $api.get<UnitLease | null>(
    `/properties/${propertyId}/units/${unitId}/lease/active`,
  );
}

/**
 * Отримати pending lease для юніта
 * GET /properties/:propertyId/units/:unitId/lease/pending
 */
export async function getPendingLeaseForUnitRequest(
  propertyId: number,
  unitId: number,
): Promise<
  AxiosResponse<{
    hasPendingLease: boolean;
    lease: UnitLease | null;
    invite: any;
  }>
> {
  return $api.get(`/properties/${propertyId}/units/${unitId}/lease/pending`);
}

/**
 * Отримати історію lease для юніта
 * GET /properties/:propertyId/units/:unitId/lease/history
 */
export async function getLeaseHistoryForUnitRequest(
  propertyId: number,
  unitId: number,
): Promise<AxiosResponse<LeaseHistoryItem[]>> {
  return $api.get<LeaseHistoryItem[]>(
    `/properties/${propertyId}/units/${unitId}/lease/history`,
  );
}

/**
 * Створити pending lease + відправити invite
 * POST /properties/:propertyId/units/:unitId/lease
 */
export async function createPendingLeaseRequest(
  propertyId: number,
  unitId: number,
  data: CreatePendingLeaseRequest,
): Promise<AxiosResponse<CreatePendingLeaseResponse>> {
  return $api.post<CreatePendingLeaseResponse>(
    `/properties/${propertyId}/units/${unitId}/lease`,
    data,
  );
}

/**
 * Скасувати pending lease
 * DELETE /properties/:propertyId/units/:unitId/lease/pending
 */
export async function deletePendingLeaseRequest(
  propertyId: number,
  unitId: number,
): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return $api.delete(`/properties/${propertyId}/units/${unitId}/lease/pending`);
}

/**
 * Завершити активний lease (owner/manager)
 * PATCH /properties/:propertyId/units/:unitId/lease/terminate
 */
export async function terminateLeaseRequest(
  propertyId: number,
  unitId: number,
  endDate?: string,
): Promise<AxiosResponse<{ success: boolean; message: string; lease: any }>> {
  return $api.patch(
    `/properties/${propertyId}/units/${unitId}/lease/terminate`,
    { endDate },
  );
}

// === LEGACY/DEPRECATED FUNCTIONS ===

/**
 * @deprecated Використовуйте нові API з units.ts
 */
export async function getUnitRequest(
  propertyId: number,
  unitId: number,
): Promise<AxiosResponse<any>> {
  return $api.get(`/properties/${propertyId}/units/${unitId}`);
}

/**
 * @deprecated Використовуйте addUnitsToPropertyRequest з units.ts
 */
export async function createUnitsNumbersRequest(
  propertyId: number,
  data: { number: string; type: string }[],
) {
  return $api.post(`/properties/${propertyId}/units`, data);
}

/**
 * @deprecated Застарілі функції юнітів
 */
export async function createUnitRequest(
  propertyId: number,
  data: any,
): Promise<AxiosResponse<any>> {
  return $api.post(`/properties/${propertyId}/unit`, data);
}

export async function updateUnitRequest(
  propertyId: number,
  unitId: number,
  data: any,
): Promise<AxiosResponse<any>> {
  return $api.patch(`/properties/${propertyId}/units/${unitId}`, data);
}

export async function deleteUnitRequest(propertyId: number, unitId: number) {
  return $api.delete(`/properties/${propertyId}/units/${unitId}`);
}

/**
 * @deprecated Застаріла логіка tenant
 */
export async function addTenantEmail(
  propertyId: number,
  unitId: number,
  email: string,
) {
  return $api.patch(`/properties/${propertyId}/units/${unitId}/email`, {
    email,
  });
}
